import type { notifications } from '@cwos/api-client';
import type { PanelNotification } from '@cwos/ui-admin';

/**
 * Notification Centre derivations. PURE — no React, no query client.
 *
 * The rule these functions exist to serve: a notification is only useful if it
 * takes you somewhere. A typed target becomes a route HERE, in one place, so
 * there is no chance of a raw URL from server data reaching a navigation call.
 */

export interface NotificationGroup {
  /** "Today", "Yesterday", or a date. */
  heading: string;
  items: notifications.Notification[];
}

const DAY_MS = 86_400_000;

/**
 * Group by day, newest first, with friendly headings for the recent ones.
 *
 * `now` is a parameter rather than read from the clock so the boundaries are
 * testable instead of only observable at midnight.
 */
export function groupByDay(
  items: readonly notifications.Notification[],
  now = new Date(),
): NotificationGroup[] {
  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);

  const groups = new Map<string, notifications.Notification[]>();
  for (const item of items) {
    const at = new Date(item.occurredAt);
    const dayStart = new Date(at);
    dayStart.setHours(0, 0, 0, 0);
    const delta = Math.round((startOfToday.getTime() - dayStart.getTime()) / DAY_MS);

    const heading =
      delta <= 0
        ? 'Today'
        : delta === 1
          ? 'Yesterday'
          : at.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' });

    const bucket = groups.get(heading);
    if (bucket) bucket.push(item);
    else groups.set(heading, [item]);
  }

  // Map preserves insertion order, and the list arrives newest-first from the
  // server, so the groups are already in the right order.
  return [...groups.entries()].map(([heading, group]) => ({ heading, items: group }));
}

/**
 * The Admin route for a typed target, or null when this app has nowhere to send
 * the reader.
 *
 * Returning null is a real answer, not a failure: a mileage trip belongs to the
 * worker who drove it and has no Admin screen of its own, so the notification
 * shows without a link rather than dumping someone on a dead page.
 *
 * Nothing here interpolates a server-supplied string into a path segment beyond
 * the id, and the id has already been validated as a UUID server-side.
 */
export function adminRouteFor(target: notifications.Notification['target']): string | null {
  if (!target) return null;
  switch (target.kind) {
    // Needs Attention opens THAT alert (`?alert=` seeds the selection).
    case 'alert':
      return `/attention?alert=${target.id}`;
    case 'channel':
      return `/messages/${target.id}`;
    case 'worker':
      return `/people`;
    case 'time_session':
      return `/sessions/${target.id}`;
    case 'organization':
      return '/settings/org';
    // MANAGEMENT SEES INTELLIGENCE. The finding detail route is
    // `/insights/$findingId` (App.tsx), and it is gated on `insights:read`
    // exactly as this destination assumes — a reader who lost the key between
    // the notification and the click meets the gate, not a broken page.
    case 'finding':
      return `/insights/${target.id}`;
    // A shift, a shift OFFERED to somebody, a message, a mileage trip and a
    // documentation item are all worker-facing objects with no Admin
    // destination. Say nothing rather than invent one. `coverage_offer` in
    // particular: the offer's management view is a drawer inside a shift's
    // coverage panel, reached from the schedule and not by id, and answering an
    // offer made to you is not an administrator's act at all.
    case 'coverage_offer':
    case 'shift':
    case 'message':
    case 'mileage_trip':
    case 'documentation_item':
      return null;
    default:
      return null;
  }
}

/**
 * The badge number.
 *
 * Capped for display so a long absence does not produce a four-digit bell that
 * breaks the shell layout. The real count is still available to anyone who opens
 * the centre.
 */
export function badgeLabel(unread: number): string | null {
  if (unread <= 0) return null;
  return unread > 99 ? '99+' : String(unread);
}

/**
 * A short relative time — "just now", "12m", "3h", "2d".
 *
 * Deliberately terse: this sits beside a title in a narrow list, and a full
 * timestamp there is noise rather than information.
 */
export function relativeAt(iso: string, now = new Date()): string {
  const ms = now.getTime() - new Date(iso).getTime();
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

/**
 * A notification as the bell's panel lists it: the title, a terse age, and the
 * SAME Admin route the Notification Centre follows, so the two cannot disagree
 * about where one leads.
 */
export function toPanelNotification(
  notification: notifications.Notification,
  now = new Date(),
): PanelNotification {
  return {
    id: notification.id,
    title: notification.title,
    age: relativeAt(notification.occurredAt, now),
    href: adminRouteFor(notification.target),
  };
}
